// components/attendance/components/EmployeeList.js
import React, { useState, useEffect } from 'react';
import Image from '../../../components/appImage';
import Button from '../../../components/ui/button';
import Icon from '../../../components/appIcon';
import Select from '../../../components/ui/select';
import Input from '../../../components/ui/input';

const BACKEND_URL = import.meta.env.VITE_API_URL;

const EmployeeList = ({ 
  employees, 
  onViewEmployee, 
  employeeSummary,
  summaryMonth,
  summaryYear,
  onSummaryMonthChange,
  onSummaryYearChange,
  onFetchEmployeeSummary
}) => {
  const [localMonth, setLocalMonth] = useState(summaryMonth);
  const [localYear, setLocalYear] = useState(summaryYear);
  
  // Generate month options
  const monthOptions = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' }
  ];
  
  // Generate year options (current year and 5 years before)
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let i = 0; i < 6; i++) {
    yearOptions.push({
      value: currentYear - i,
      label: (currentYear - i).toString()
    });
  }
  
  // Apply filters
  const handleApplyFilters = () => {
    onSummaryMonthChange(localMonth);
    onSummaryYearChange(localYear);
    onFetchEmployeeSummary(localMonth, localYear);
  };
  
  // Download CSV function
  const handleDownloadCSV = () => {
    // Create CSV content
    let csvContent = "Employee Name,Month,Year,No. of Present,No. of Absent,No. of Leave,No. of Hours\n";
    
    employeeSummary.forEach(employee => {
      csvContent += `${employee.employee_name},${employee.month},${employee.year},${employee.no_present},${employee.no_absent},${employee.no_leave},${employee.no_hours}\n`;
    });
    
    // Create a download link
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `employee_attendance_summary_${localMonth}_${localYear}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Merge employee data with summary data
  const mergedData = employees.map(employee => {
    const summary = employeeSummary.find(s => s.employee_id === employee.employee_id) || {
      employee_id: employee.employee_id,
      employee_name: `${employee.first_name} ${employee.last_name}`,
      month: localMonth,
      year: localYear,
      no_present: 0,
      no_absent: 0,
      no_leave: 0,
      no_hours: "0h 0m"
    };
    
    return {
      ...employee,
      ...summary
    };
  });
  
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Filters Section */}
      <div className="p-4 border-b border-border bg-muted/30">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          {/* <div className="flex-1 min-w-[150px]"> */}
            <Select
              options={monthOptions}
              value={localMonth}
              onChange={(value) => setLocalMonth(value)}
              className="w-full"
              // label="Month"
            />
          {/* </div> */}
          
          {/* <div className="flex-1 min-w-[150px]"> */}
            <Select
              options={yearOptions}
              value={localYear}
              onChange={(value) => setLocalYear(value)}
              className="w-full"
              // label="Year"
            />
          {/* </div> */}
          
          <div className="flex gap-2">
            <Button
              onClick={handleApplyFilters}
              className="flex items-center gap-2"
            >
              <Icon name="Filter" size={16} />
              Apply Filters
            </Button>
            
            <Button
              variant="outline"
              onClick={handleDownloadCSV}
              className="flex items-center gap-2"
            >
              <Icon name="Download" size={16} />
              Download CSV
            </Button>
          </div>
        </div>
      </div>
      
      {/* Table Section */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="text-left p-3">Employee Name</th>
              <th className="text-left p-3">Month</th>
              <th className="text-left p-3">Year</th>
              <th className="text-left p-3">No. of Present</th>
              <th className="text-left p-3">No. of Absent</th>
              <th className="text-left p-3">No. of Leave</th>
              <th className="text-left p-3">No. of Hours</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {mergedData.map(employee => (
              <tr key={employee.id} className="border-b border-border hover:bg-muted/50">
                <td className="p-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-full overflow-hidden">
                      <Image
                        src={`${BACKEND_URL}${employee?.image1}`}
                        alt={`${employee.first_name} ${employee.last_name}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <div className="font-medium text-foreground">
                        {employee.first_name} {employee.last_name}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {employee.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="p-3">{monthOptions.find(m => m.value === employee.month)?.label || employee.month}</td>
                <td className="p-3">{employee.year}</td>
                <td className="p-3">{employee.no_present}</td>
                <td className="p-3">{employee.no_absent}</td>
                <td className="p-3">{employee.no_leave}</td>
                <td className="p-3">{employee.no_hours}</td>
                <td className="p-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onViewEmployee(employee)}
                    className="flex items-center space-x-1"
                  >
                    <Icon name="Eye" size={16} />
                    <span>View Attendance</span>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {employees.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No employees found</h3>
          <p className="text-muted-foreground">There are no employees to display.</p>
        </div>
      )}
    </div>
  );
};

export default EmployeeList;